import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'
import { toast } from 'react-toastify'
import defaultImg from '../../assets/img/gl-1.png'

interface Property {
    _id: string;
    propertyCategory?: string;
    subCategory?: string;
    propertyType: string;
    price: number;
    priceMin?: number;
    priceMax?: number;
    size?: number;
    buildingName?: string;
    area: string;
    city?: string;
    nearbyAddress?: string;
    bhkConfiguration?: string;
    furnishingType?: string;
    description?: string;
    createdAt: string;
    actionType?: string;
    owner?: { name: string; email: string; role: string };
    status?: string;
    contactName?: string;
    contactNumber?: string;
    contactEmail?: string;
    postedBy?: string;
    occupancyType?: string;
    propertyAge?: string;
    floor?: string;
    washrooms?: number;
    totalUnit?: string;
    facing?: string;
    noOfParking?: string;
    images?: string[];
}

const statusOptions = ['Active', 'Not Answer', 'Rent out', 'Data Mismatch', 'Broker', 'Duplicate'];

export default function AdminSavedProperties() {
    const { token } = useAuth()
    const [properties, setProperties] = useState<Property[]>([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('Owner Property')
    const [dateFilter, setDateFilter] = useState('All Time')
    const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null)
    const [openDropdown, setOpenDropdown] = useState<'category' | 'date' | null>(null);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [defaultImages, setDefaultImages] = useState<any[]>([]);

    useEffect(() => {
        if (!token) { setLoading(false); return }
        // Fetch properties
        fetch(`${API_BASE}/classified-properties`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setProperties(data) })
            .catch(console.error)
            .finally(() => setLoading(false))

        // Fetch default images
        fetch(`${API_BASE}/default-lead-images`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setDefaultImages(data) })
            .catch(console.error)
    }, [token])

    const handleUpdateStatus = async (id: string, newStatus: string) => {
        try {
            const res = await fetch(`${API_BASE}/classified-properties/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ status: newStatus })
            });
            if (res.ok) {
                setProperties(prev => prev.map(p => p._id === id ? { ...p, status: newStatus } : p));
                toast.success('Status updated successfully');
            }
        } catch (error) {
            console.error('Failed to update status', error);
            toast.error('Failed to update status');
        } finally {
            setActiveDropdownId(null);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this property?')) return;

        try {
            const res = await fetch(`${API_BASE}/classified-properties/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                setProperties(prev => prev.filter(p => p._id !== id));
                toast.success('Property deleted successfully');
            } else {
                toast.error('Failed to delete property');
            }
        } catch (error) {
            console.error('Failed to delete property', error);
            toast.error('Error deleting property');
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected properties?`)) return;

        try {
            const results = await Promise.all(selectedIds.map(async id => {
                const res = await fetch(`${API_BASE}/classified-properties/${id}`, {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` }
                });
                return { id, ok: res.ok };
            }));
            
            const successfulIds = results.filter(r => r.ok).map(r => r.id);
            const failedCount = results.length - successfulIds.length;

            if (successfulIds.length > 0) {
                setProperties(prev => prev.filter(p => !successfulIds.includes(p._id)));
                setSelectedIds(prev => prev.filter(id => !successfulIds.includes(id)));
                toast.success(`${successfulIds.length} properties deleted successfully`);
            }
            if (failedCount > 0) {
                toast.error(`Failed to delete ${failedCount} properties. They might be unauthorized.`);
            }
        } catch (error) {
            console.error('Failed to delete properties', error);
            toast.error('Error deleting some properties');
        }
    };

    const getShareText = (p: Property) => {
        const displayPrice = p.price 
            ? `₹${p.price.toLocaleString()}`
            : p.priceMin && p.priceMax 
                ? `₹${p.priceMin.toLocaleString()} - ₹${p.priceMax.toLocaleString()}`
                : 'Contact for price';

        return `🏡 *New Property Available!* 🏡

*Property Type:* ${p.propertyCategory || p.propertyType}
*Subtype:* ${p.subCategory || 'N/A'}
*Building/Premise:* ${p.buildingName || 'N/A'}
*Size/BHK:* ${p.bhkConfiguration || 'N/A'}
*Area:* ${p.size ? `${p.size} sqft` : 'N/A'}
*Location:* ${p.nearbyAddress ? `${p.nearbyAddress}, ` : ''}${p.city || 'N/A'}
*Condition:* ${p.furnishingType || 'N/A'}

💰 *Value:* ${displayPrice}

*Description:*
${p.description || 'No description provided.'}

Check it out now on My Property Guru!`;
    };

    const handleShare = async (p: Property) => {
        const text = getShareText(p);
        try {
            if (navigator.share) {
                await navigator.share({
                    title: 'Property Listing - My Property Guru',
                    text: text,
                });
            } else {
                await navigator.clipboard.writeText(text);
                toast.success('Property details copied to clipboard!');
            }
        } catch (err) {
            console.error('Error sharing property:', err);
            if (err instanceof Error && err.name !== 'AbortError') {
                 toast.error('Failed to share property details.');
            }
        }
    };

    const handleWhatsAppShare = (p: Property) => {
        const text = getShareText(p);
        const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
        window.open(whatsappUrl, '_blank');
    };

    const filtered = properties.filter(p => {
        const postedBy = (p.postedBy || '').toLowerCase();
        const ownerRole = (p.owner?.role || 'owner').toLowerCase()
        
        // Search filtering
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchSearch = 
                (p._id && p._id.toLowerCase().includes(query)) ||
                (p.buildingName && p.buildingName.toLowerCase().includes(query)) ||
                (p.area && p.area.toLowerCase().includes(query)) ||
                (p.city && p.city.toLowerCase().includes(query)) ||
                (p.propertyType && p.propertyType.toLowerCase().includes(query)) ||
                (postedBy && postedBy.includes(query));
            if (!matchSearch) return false;
        }

        // Tab filtering
        let tabMatch = false;
        if (activeTab === 'All Properties') {
            tabMatch = true;
        } else if (activeTab === 'Owner Property') {
            if (postedBy) tabMatch = postedBy === 'owner';
            else tabMatch = ownerRole === 'owner' || ownerRole === 'admin'
        } else if (activeTab === 'Broker Property') {
            if (postedBy) tabMatch = postedBy === 'broker' || postedBy === 'agent';
            else tabMatch = ownerRole === 'broker' || ownerRole === 'agent'
        } else {
            tabMatch = p.actionType === 'post_requirement' || p.propertyType?.includes('Requirement')
        }

        if (!tabMatch) return false;

        // Date filtering
        if (dateFilter === 'All Time') return true;
        
        const createdAt = new Date(p.createdAt);
        const now = new Date();
        
        // Reset time for calendar comparison
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const createdDate = new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate());
        const diffTime = today.getTime() - createdDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (dateFilter === 'Today') return diffDays === 0;
        if (dateFilter === 'Last 7 Days') return diffDays <= 7;
        if (dateFilter === 'Last 30 Days') return diffDays <= 30;

        return true;
    })

    return (
        <AdminLayout>
            {/* Header / Actions section */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-10 select-none">
                <div>
                    <h1 className="text-3xl font-bold text-[#0f172a] mb-2 leading-none">Classified & Saved</h1>
                    <p className="text-slate-500 font-medium text-sm">All listings posted directly by owners and verified brokers.</p>
                </div>

                {/* Filter Group */}
                <div className="flex items-center gap-3 flex-nowrap shrink-0">
                    {/* Search Bar */}
                    <div className="relative">
                        <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                        <input
                            type="text"
                            placeholder="Search by ID, name, area..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-full text-xs font-bold w-full sm:w-64 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all shadow-sm"
                        />
                    </div>

                    {/* Property Category Dropdown */}
                    <div className="relative select-none">
                        <div 
                            onClick={() => setOpenDropdown(openDropdown === 'category' ? null : 'category')}
                            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-100 rounded-full shadow-sm cursor-pointer hover:bg-slate-50 transition-all"
                        >
                            <i className="fa-solid fa-house-circle-check text-[#05144b] text-sm"></i>
                            <span className="text-xs font-bold text-[#05144b]">{activeTab}</span>
                            <i className={`fa-solid fa-chevron-down text-[10px] text-slate-400 transition-transform duration-300 ${openDropdown === 'category' ? 'rotate-180' : ''}`}></i>
                        </div>
                        
                        {openDropdown === 'category' && (
                            <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-slate-100 shadow-xl rounded-2xl overflow-hidden z-30 animate-in fade-in slide-in-from-top-2 duration-200">
                                {['All Properties', 'Owner Property', 'Broker Property', 'Requirements'].map((tab) => (
                                    <button
                                        key={tab}
                                        onClick={() => {
                                            setActiveTab(tab);
                                            setOpenDropdown(null);
                                        }}
                                        className={`w-full text-left px-5 py-3 text-xs font-bold transition-all ${activeTab === tab ? 'bg-indigo-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Date Filter Dropdown */}
                    <div className="relative select-none">
                        <div 
                            onClick={() => setOpenDropdown(openDropdown === 'date' ? null : 'date')}
                            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-100 rounded-full shadow-sm cursor-pointer hover:bg-slate-50 transition-all"
                        >
                            <i className="fa-solid fa-calendar-day text-[#05144b] text-sm"></i>
                            <span className="text-xs font-bold text-[#05144b]">{dateFilter}</span>
                            <i className={`fa-solid fa-chevron-down text-[10px] text-slate-400 transition-transform duration-300 ${openDropdown === 'date' ? 'rotate-180' : ''}`}></i>
                        </div>
                        
                        {openDropdown === 'date' && (
                            <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-slate-100 shadow-xl rounded-2xl overflow-hidden z-30 animate-in fade-in slide-in-from-top-2 duration-200">
                                {['All Time', 'Today', 'Last 7 Days', 'Last 30 Days'].map((filter) => (
                                    <button
                                        key={filter}
                                        onClick={() => {
                                            setDateFilter(filter);
                                            setOpenDropdown(null);
                                        }}
                                        className={`w-full text-left px-5 py-3 text-xs font-bold transition-all ${dateFilter === filter ? 'bg-indigo-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                                    >
                                        {filter}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

            </div>

            {/* Main content body / Card list */}
            {loading ? (
                <div className="p-32 text-center">
                    <div className="w-12 h-12 border-4 border-[#5B32B4] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                    <p className="text-[#5B32B4] font-bold text-xs animate-pulse">Syncing listings...</p>
                </div>
            ) : filtered.length === 0 ? (
                <div className="p-32 text-center select-none bg-white rounded-2xl border border-slate-100 shadow-sm">
                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-100">
                        <i className="fa-solid fa-building-circle-exclamation text-3xl text-slate-400"></i>
                    </div>
                    <h3 className="text-lg font-bold text-slate-700 mb-2">No properties yet</h3>
                    <p className="text-slate-400 text-xs font-medium max-w-xs mx-auto">Properties matching {activeTab} will appear here once listed.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Bulk Actions Toolbar */}
                    <div className="flex items-center justify-between bg-white px-6 py-4 rounded-2xl border border-slate-100 shadow-sm mb-6">
                        <div className="flex items-center gap-3">
                            <input 
                                type="checkbox" 
                                className="w-5 h-5 accent-indigo-600 rounded cursor-pointer"
                                checked={selectedIds.length === filtered.length && filtered.length > 0}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedIds(filtered.map(p => p._id))
                                    } else {
                                        setSelectedIds([])
                                    }
                                }}
                            />
                            <span className="text-sm font-bold text-slate-700">Select All ({filtered.length})</span>
                        </div>
                        {selectedIds.length > 0 && (
                            <button 
                                onClick={handleBulkDelete}
                                className="bg-rose-50 text-rose-600 px-4 py-2 rounded-xl text-xs font-bold border border-rose-100 hover:bg-rose-100 transition-all flex items-center gap-2 shadow-sm"
                            >
                                <i className="fa-solid fa-trash-can"></i> Delete Selected ({selectedIds.length})
                            </button>
                        )}
                    </div>

                    {filtered.map(p => {
                        const dateObj = p.createdAt ? new Date(p.createdAt) : new Date();
                        const displayDate = `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear()}`;
                        const displayPrice = p.price 
                            ? `₹${p.price.toLocaleString()}`
                            : p.priceMin && p.priceMax 
                                ? `₹${p.priceMin.toLocaleString()} - ₹${p.priceMax.toLocaleString()}`
                                : 'Contact for price';

                        let pType = (p.propertyCategory || p.propertyType || '').toLowerCase();
                        const pSub = (p.subCategory || '').toLowerCase();

                        // Map specific types to broad categories if necessary
                        const residentialTypes = ['apartment', 'villa', 'house', 'residential', 'flat', 'duplex', 'penthouse', 'studio'];
                        const commercialTypes = ['commercial', 'office', 'shop', 'warehouse', 'industrial', 'factory'];
                        const landTypes = ['plot', 'land'];

                        if (residentialTypes.includes(pType)) pType = 'residential';
                        else if (commercialTypes.includes(pType)) pType = 'commercial';
                        else if (landTypes.includes(pType)) pType = 'land';

                        const matchDefault = defaultImages.find(di => {
                            const diType = (di.propertyType || '').toLowerCase();
                            const diSub = (di.subCategory || '').toLowerCase();
                            return diType === pType && (diSub.includes(pSub) || pSub.includes(diSub));
                        });
                        const fallbackImg = matchDefault && (matchDefault.imageUrl || matchDefault.image) ? getImageUrl(matchDefault.imageUrl || matchDefault.image) : defaultImg;
                        const coverImg = p.images && p.images.length > 0 ? getImageUrl(p.images[0]) : fallbackImg;

                        return (
                            <div key={p._id} className="bg-white border border-slate-100 rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-all">
                                {/* Top Row / Card Header */}
                                <div className="flex flex-wrap items-center justify-between gap-4 mb-6 select-none">
                                    <div className="flex flex-wrap items-center gap-3">
                                        {/* Selection Checkbox */}
                                        <div className="flex items-center justify-center mr-2">
                                            <input 
                                                type="checkbox" 
                                                className="w-5 h-5 accent-indigo-600 rounded cursor-pointer"
                                                checked={selectedIds.includes(p._id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedIds(prev => [...prev, p._id])
                                                    } else {
                                                        setSelectedIds(prev => prev.filter(id => id !== p._id))
                                                    }
                                                }}
                                            />
                                        </div>

                                        {/* Date pill */}
                                        <div className="flex items-center gap-1.5 bg-red-50 text-red-500 font-bold text-xs px-3.5 py-2 rounded-full border border-red-100">
                                            <span className="w-2 h-2 bg-emerald-500 rounded-full inline-block"></span>
                                            {displayDate}
                                        </div>

                                        {/* ID */}
                                        <div className="bg-blue-50 text-blue-600 font-bold text-xs px-3.5 py-2 rounded-full border border-blue-100">
                                            ID: {p._id}
                                        </div>
                                        
                                        {/* Posted By */}
                                        {p.postedBy && (
                                            <div className="bg-orange-50 text-orange-600 font-bold text-xs px-3.5 py-2 rounded-full border border-orange-100">
                                                Posted By: {p.postedBy}
                                            </div>
                                        )}

                                        {/* Premise Name */}
                                        {p.buildingName && (
                                            <div className="bg-purple-50 text-purple-600 font-bold text-xs px-4 py-2 rounded-full border border-purple-100">
                                                Premise - {p.buildingName}
                                            </div>
                                        )}

                                        {/* Price / Rent */}
                                        <div className="bg-blue-50 text-blue-600 font-bold text-xs px-4 py-2 rounded-full border border-blue-100">
                                            Value: {displayPrice}
                                        </div>
                                    </div>

                                    {/* Top Right Actions */}
                                    <div className="flex items-center gap-3">
                                        <button 
                                            onClick={() => handleWhatsAppShare(p)}
                                            className="w-9 h-9 flex items-center justify-center bg-[#25D366] hover:bg-[#128C7E] text-white rounded-xl transition-all shadow-sm cursor-pointer"
                                            title="Share on WhatsApp"
                                        >
                                            <i className="fa-brands fa-whatsapp text-lg"></i>
                                        </button>

                                        <button 
                                            onClick={() => handleShare(p)}
                                            className="w-9 h-9 flex items-center justify-center bg-indigo-900 hover:bg-indigo-950 text-white rounded-xl transition-all shadow-sm cursor-pointer"
                                            title="Share Property"
                                        >
                                            <i className="fa-solid fa-share-nodes text-sm"></i>
                                        </button>

                                        <button 
                                            onClick={() => handleDelete(p._id)}
                                            className="w-9 h-9 flex items-center justify-center bg-rose-50 hover:bg-rose-100 text-rose-500 rounded-xl border border-rose-100 transition-all shadow-sm cursor-pointer"
                                            title="Delete Property"
                                        >
                                            <i className="fa-solid fa-trash-can text-sm"></i>
                                        </button>

                                        <Link 
                                            to={`/admin-edit-classified-property/${p._id}`}
                                            className="w-9 h-9 flex items-center justify-center bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl border border-blue-100 transition-all shadow-sm cursor-pointer"
                                            title="Edit Property"
                                        >
                                            <i className="fa-solid fa-pen-to-square text-sm"></i>
                                        </Link>

                                        {/* Status Dropdown - Moved to Last */}
                                        <div className="relative">
                                            <div 
                                                onClick={() => setActiveDropdownId(activeDropdownId === p._id ? null : p._id)}
                                                className={`flex items-center gap-2 font-bold text-xs px-3.5 py-2 rounded-xl border cursor-pointer transition-all ${(p.status || 'Active') === 'Active' ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-600 border-emerald-100' : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'}`}
                                            >
                                                {p.status || 'Active'} <i className={`fa-solid fa-chevron-${activeDropdownId === p._id ? 'up' : 'down'} text-xs`}></i>
                                            </div>
                                            
                                            {activeDropdownId === p._id && (
                                                <>
                                                    <div className="fixed inset-0 z-10" onClick={() => setActiveDropdownId(null)}></div>
                                                    <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-slate-100 shadow-xl rounded-xl overflow-hidden z-20 flex flex-col py-1">
                                                        {statusOptions.map(opt => (
                                                            <button 
                                                                key={opt}
                                                                onClick={() => handleUpdateStatus(p._id, opt)}
                                                                className={`text-left px-4 py-3 text-xs font-bold transition-all ${(p.status || 'Active') === opt ? 'bg-[#5B32B4]/5 text-[#5B32B4]' : 'text-slate-600 hover:bg-slate-50'}`}
                                                            >
                                                                {opt}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Card Column Contents */}
                                <div className="flex flex-col md:flex-row gap-8 border-t border-slate-100 pt-6">
                                    {/* Column 0: Image */}
                                    <div className="w-full md:w-64 h-48 md:h-auto shrink-0 rounded-2xl overflow-hidden border border-slate-100 shadow-sm relative group cursor-pointer">
                                        <img src={coverImg} alt="Property" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent"></div>
                                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                                            <span className="bg-indigo-600/90 text-white text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider backdrop-blur-sm shadow-sm">{p.subCategory || p.propertyType}</span>
                                            {p.images && p.images.length > 1 && (
                                                <span className="bg-black/50 text-white text-[10px] font-black px-2 py-1 rounded-full backdrop-blur-sm shadow-sm">
                                                    <i className="fa-regular fa-images mr-1"></i>{p.images.length}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* Column 1: Core details */}
                                        <div className="flex flex-col select-none divide-y divide-slate-100">
                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-building text-amber-500 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Property Type</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.propertyCategory || p.propertyType}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-city text-teal-500 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Subtype</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.subCategory || 'N/A'}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-map-pin text-slate-700 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Area/Locality</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.area}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-vector-square text-indigo-500 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Size (Sq.Ft)</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.size ? `${p.size} sqft` : 'N/A'}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-bed text-amber-500 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">BHK Config</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.bhkConfiguration || 'N/A'}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-users text-purple-500 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Occupancy</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.occupancyType || 'N/A'}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-calendar-check text-blue-500 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Property Age</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.propertyAge || 'N/A'}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Column 2: Additional details */}
                                    <div className="flex flex-col select-none divide-y divide-slate-100 pl-0 md:pl-8 border-l border-transparent md:border-slate-200">
                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-layer-group text-slate-700 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Floor Number</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.floor || 'N/A'}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-toilet text-blue-400 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Washrooms</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.washrooms || 'N/A'}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-building-circle-check text-teal-600 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Total Units</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.totalUnit || 'N/A'}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-compass text-orange-500 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Facing</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.facing || 'N/A'}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-car text-slate-500 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Parking Count</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.noOfParking || 'N/A'}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-couch text-rose-500 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Furnishing</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.furnishingType || 'N/A'}</span>
                                        </div>

                                        <div className="flex items-center justify-between py-3.5">
                                            <div className="flex items-center gap-3">
                                                <i className="fa-solid fa-location-dot text-emerald-600 text-base w-5 text-center"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">City</span>
                                            </div>
                                            <span className="text-xs font-bold text-slate-900">{p.city || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                                {/* Full Width: Description & Contacts */}
                                <div className="mt-8 pt-8 border-t border-slate-100">
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                                        <div className="lg:col-span-2">
                                            <div className="flex items-center gap-3 mb-4">
                                                <i className="fa-solid fa-file-lines text-emerald-600 text-base"></i>
                                                <span className="text-xs font-bold text-[#0f172a]">Property Description</span>
                                            </div>
                                            <p className="text-sm text-slate-900 leading-relaxed font-medium">
                                                {p.description || 'No detailed description provided for this listing.'}
                                            </p>
                                        </div>


                                        <div className="lg:col-span-3">
                                            <div className="flex flex-wrap items-center gap-x-12 gap-y-6">
                                                <div className="flex items-center gap-3 pr-6 border-r border-slate-100">
                                                    <i className="fa-solid fa-address-book text-purple-600 text-base"></i>
                                                    <span className="text-xs font-bold text-[#0f172a]">Contacts:</span>
                                                </div>
                                                
                                                <div className="flex flex-col min-w-[120px]">
                                                    <span className="text-[10px] font-bold text-slate-600 tracking-tight">Contact Name</span>
                                                    <span className="text-xs font-bold text-[#05144b]">{p.contactName || p.owner?.name || 'N/A'}</span>
                                                </div>
                                                
                                                <div className="flex flex-col min-w-[120px]">
                                                    <span className="text-[10px] font-bold text-slate-600 tracking-tight">Phone Number</span>
                                                    <span className="text-xs font-bold text-[#05144b]">{p.contactNumber || 'N/A'}</span>
                                                </div>
                                                
                                                <div className="flex flex-col min-w-[120px]">
                                                    <span className="text-[10px] font-bold text-slate-600 tracking-tight">Email Address</span>
                                                    <span className="text-xs font-bold text-[#05144b] lowercase">{p.contactEmail || p.owner?.email || 'N/A'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </AdminLayout>
    )
}
